import React, { ForwardedRef, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Box, Button, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import ServiceAPI from '../../services/serviceapi';
import tokenService from '../../services/tokenservice';

const C = {
  marinho: '#1d2b4b',
  marinhoClaro: '#2a3d68',
  verde: '#82b07d',
  verdeEscuro: '#5e8f59',
  verdeAlpha: 'rgba(130,176,125,0.12)',
  branco: '#ffffff',
  cinzaFundo: '#f5f7fa',
  cinzaBorda: '#e1e7ef',
  cinzaTexto: '#64748b',
  erro: '#e05252',
  erroAlpha: 'rgba(224,82,82,0.1)',
};

type Estado = 'aguardando' | 'processando' | 'sucesso' | 'falha';

const ETAPAS_PROCESSAMENTO = [
  { label: 'Validando chave de confirmação', duracao: 900 },
  { label: 'Criando seu ambiente isolado', duracao: 1400 },
  { label: 'Configurando permissões e licença', duracao: 1100 },
  { label: 'Finalizando sua conta', duracao: 800 },
];

const ConfirmacaoCadastro = React.forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
  const [hashConfirmacao, setHashConfirmacao] = useState<string | null>(null);
  const [estado, setEstado] = useState<Estado>('processando');
  const [tentativa, setTentativa] = useState(0);
  const [etapaVisual, setEtapaVisual] = useState(0);
  const [urlDestino, setUrlDestino] = useState<string | null>(null);
  const etapaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jaConfirmouRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location?.search?.length > 0) {
      const raw = location.search;
      const cleanToken = raw.startsWith('?') ? raw.substring(1) : raw;
      setHashConfirmacao(cleanToken);
    } else {
      setEstado('aguardando');
    }
  }, [location]);

  useEffect(() => {
    if (hashConfirmacao && !jaConfirmouRef.current) {
      jaConfirmouRef.current = true;
      iniciarEtapasVisuais();
      processarConfirmacao(0);
    }
  }, [hashConfirmacao]);

  const iniciarEtapasVisuais = () => {
    let acumulado = 0;
    ETAPAS_PROCESSAMENTO.forEach((etapa, i) => {
      const timer = setTimeout(() => setEtapaVisual(i), acumulado);
      etapaTimerRef.current = timer;
      acumulado += etapa.duracao;
    });
  };

  const processarConfirmacao = async (tentativaAtual: number) => {
    setTentativa(tentativaAtual);

    try {
      const result = await ServiceAPI.consumeService({
        url: '/public/confirmarcadastro',
        data: hashConfirmacao,
        config: { ignoreToast: true },
      });

      if (result?.dataResponse) {
        setUrlDestino(result.dataResponse);
        setEstado('sucesso');
        toast.success('Cadastro confirmado com sucesso!');

        setTimeout(async () => {
          await tokenService.deleteToken();
          navigate(`/login?xs=${result.dataResponse}`);
        }, 3500);
        return;
      }

      throw new Error('Sem retorno');
    } catch {
      if (tentativaAtual < 4) {
        const delay = 1200 + tentativaAtual * 400;
        setTimeout(() => processarConfirmacao(tentativaAtual + 1), delay);
      } else {
        setEstado('falha');
      }
    }
  };

  const etapaAtual = ETAPAS_PROCESSAMENTO[etapaVisual];

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: C.cinzaFundo,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: { xs: '24px 16px', md: '48px' },
    }}>
      <Box sx={{ width: '100%', maxWidth: 480 }}>

        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <img src="/AppCodesh.png" alt="App Codesh" style={{ height: 34 }} />
        </Box>

        {/* Card */}
        <Box sx={{
          bgcolor: C.branco, borderRadius: '20px',
          border: `1px solid ${C.cinzaBorda}`,
          boxShadow: '0 4px 24px rgba(29,43,75,0.07)',
          overflow: 'hidden',
        }}>

          {/* ─── Aguardando (sem hash) ─── */}
          {estado === 'aguardando' && (
            <Box sx={{ p: { xs: '32px 24px', md: '48px 40px' }, textAlign: 'center' }}>
              <Box sx={{ fontSize: 52, mb: 2 }}>📬</Box>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.marinho, mb: 1 }}>
                Verifique seu e-mail
              </Typography>
              <Typography sx={{ fontSize: 14, color: C.cinzaTexto, lineHeight: 1.7, mb: 3 }}>
                Enviamos um link de confirmação para o seu e-mail. Clique nele para ativar sua conta.
              </Typography>
              <Box sx={{
                bgcolor: C.verdeAlpha, borderRadius: '12px', p: '14px 18px',
                display: 'flex', gap: 1.5, alignItems: 'flex-start', textAlign: 'left', mb: 3,
              }}>
                <Typography sx={{ fontSize: 16 }}>💡</Typography>
                <Typography sx={{ fontSize: 13, color: C.marinho, lineHeight: 1.6 }}>
                  Não encontrou o e-mail? Verifique a pasta de spam ou lixo eletrônico.
                  O link expira em 24 horas.
                </Typography>
              </Box>
              <Button
                onClick={() => navigate('/cadastro')}
                sx={{
                  borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                  border: `1px solid ${C.cinzaBorda}`, color: C.cinzaTexto,
                  '&:hover': { bgcolor: C.cinzaFundo },
                }}
              >
                Voltar ao cadastro
              </Button>
            </Box>
          )}

          {/* ─── Processando ─── */}
          {estado === 'processando' && (
            <Box sx={{ p: { xs: '32px 24px', md: '48px 40px' } }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.marinho, mb: 0.5 }}>
                Ativando sua conta
              </Typography>
              <Typography sx={{ fontSize: 13, color: C.cinzaTexto, mb: 4 }}>
                Aguarde enquanto preparamos tudo para você
              </Typography>

              <Stack spacing={2}>
                {ETAPAS_PROCESSAMENTO.map((etapa, i) => {
                  const concluida = i < etapaVisual;
                  const atual = i === etapaVisual;
                  const pendente = i > etapaVisual;

                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: concluida ? C.verde : atual ? C.marinho : C.cinzaBorda,
                        transition: 'all 0.4s ease',
                      }}>
                        {concluida && (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {atual && (
                          <Box sx={{
                            width: 10, height: 10, borderRadius: '50%', bgcolor: C.branco,
                            animation: 'pulse 1.2s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                              '50%': { transform: 'scale(0.7)', opacity: 0.6 },
                            },
                          }} />
                        )}
                        {pendente && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.cinzaTexto, opacity: 0.3 }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{
                          fontSize: 14, fontWeight: atual ? 600 : 400,
                          color: concluida ? C.verde : atual ? C.marinho : C.cinzaTexto,
                          transition: 'color 0.3s',
                        }}>
                          {etapa.label}
                        </Typography>
                        {atual && (
                          <Box sx={{
                            mt: 0.75, height: 3, borderRadius: '2px',
                            bgcolor: C.cinzaBorda, overflow: 'hidden',
                          }}>
                            <Box sx={{
                              height: '100%', bgcolor: C.verde, borderRadius: '2px',
                              animation: 'progresso 1.4s ease-in-out infinite',
                              '@keyframes progresso': {
                                '0%': { width: '0%', marginLeft: '0%' },
                                '50%': { width: '70%', marginLeft: '15%' },
                                '100%': { width: '0%', marginLeft: '100%' },
                              },
                            }} />
                          </Box>
                        )}
                      </Box>

                      {concluida && (
                        <Typography sx={{ fontSize: 12, color: C.verde, fontWeight: 600 }}>Pronto</Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>

              {tentativa > 0 && (
                <Box sx={{ mt: 3, p: '10px 14px', bgcolor: C.verdeAlpha, borderRadius: '10px' }}>
                  <Typography sx={{ fontSize: 12, color: C.marinho }}>
                    Estabilizando conexão... tentativa {tentativa + 1} de 5
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ─── Sucesso ─── */}
          {estado === 'sucesso' && (
            <Box sx={{ p: { xs: '32px 24px', md: '48px 40px' }, textAlign: 'center' }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: C.verdeAlpha, display: 'flex', alignItems: 'center',
                justifyContent: 'center', mx: 'auto', mb: 3,
                border: `2px solid ${C.verde}`,
                animation: 'popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '@keyframes popIn': {
                  '0%': { transform: 'scale(0)', opacity: 0 },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
              }}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M6 16L13 23L26 9" stroke={C.verde} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>

              <Typography sx={{ fontSize: 24, fontWeight: 700, color: C.marinho, mb: 1 }}>
                Conta ativada!
              </Typography>
              <Typography sx={{ fontSize: 14, color: C.cinzaTexto, lineHeight: 1.7, mb: 3 }}>
                Seu ambiente está pronto. Você receberá um e-mail com seus dados de acesso.
                Redirecionando para o login...
              </Typography>

              {urlDestino && (
                <Box sx={{
                  bgcolor: C.verdeAlpha, borderRadius: '12px', p: '12px 16px',
                  border: `1px solid ${C.verde}44`, mb: 3,
                }}>
                  <Typography sx={{ fontSize: 11, color: C.cinzaTexto, mb: 0.25 }}>Sua plataforma</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.marinho }}>
                    gestao.appcodesh.com.br/{urlDestino}
                  </Typography>
                </Box>
              )}

              <Box sx={{
                height: 4, borderRadius: '2px', bgcolor: C.cinzaBorda, overflow: 'hidden',
              }}>
                <Box sx={{
                  height: '100%', bgcolor: C.verde, borderRadius: '2px',
                  animation: 'countdown 3.5s linear forwards',
                  '@keyframes countdown': { '0%': { width: '100%' }, '100%': { width: '0%' } },
                }} />
              </Box>
            </Box>
          )}

          {/* ─── Falha ─── */}
          {estado === 'falha' && (
            <Box sx={{ p: { xs: '32px 24px', md: '48px 40px' }, textAlign: 'center' }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: C.erroAlpha, display: 'flex', alignItems: 'center',
                justifyContent: 'center', mx: 'auto', mb: 3,
                border: `2px solid ${C.erro}44`,
              }}>
                <Typography sx={{ fontSize: 30 }}>⚠️</Typography>
              </Box>

              <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.marinho, mb: 1 }}>
                Não conseguimos confirmar
              </Typography>
              <Typography sx={{ fontSize: 14, color: C.cinzaTexto, lineHeight: 1.7, mb: 3 }}>
                O link pode ter expirado, já ter sido utilizado, ou houve uma instabilidade temporária.
                Tente clicar novamente no link do e-mail.
              </Typography>

              <Stack spacing={1.5}>
                <Button
                  fullWidth
                  onClick={() => {
                    jaConfirmouRef.current = false;
                    setEtapaVisual(0);
                    setEstado('processando');
                    iniciarEtapasVisuais();
                    processarConfirmacao(0);
                  }}
                  sx={{
                    py: 1.4, borderRadius: '10px', fontWeight: 700, textTransform: 'none',
                    bgcolor: C.marinho, color: C.branco,
                    '&:hover': { bgcolor: C.marinhoClaro },
                  }}
                >
                  Tentar novamente
                </Button>
                <Button
                  fullWidth
                  onClick={() => navigate('/cadastro')}
                  sx={{
                    py: 1.4, borderRadius: '10px', fontWeight: 600, textTransform: 'none',
                    border: `1px solid ${C.cinzaBorda}`, color: C.cinzaTexto,
                    '&:hover': { bgcolor: C.cinzaFundo },
                  }}
                >
                  Voltar ao cadastro
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default ConfirmacaoCadastro;